<script setup>
import { useI18n } from 'vue-i18n'
import TrackName from '@/components/TrackName.vue'
import { podiumClass } from '@/lib/podium'

/**
 * The personal bests of one player: their fastest lap on every track they set a time on, with the
 * car it was done in and the place it is worth in the all-time leaderboard of that track. The
 * footer sends to the leaderboard pages.
 *
 * Only the active tracks arrive here (see FastestLapLib::personalBests): a retired one is no longer
 * a track the player can race.
 *
 * It only renders the list it is given; a player with no time at all has no row, and the caller
 * leaves the block out (see PlayerView).
 */
defineProps({
  /** One row per track, in the shape of GET /api/players/{username}. */
  laps: { type: Array, default: () => [] },
})

const { t } = useI18n()
</script>

<template>
  <v-card elevation="3" rounded="lg" class="overflow-hidden">
    <div class="d-flex align-center ga-3 pa-4">
      <v-avatar color="primary" variant="tonal" size="48">
        <v-icon size="28">mdi-timer-star-outline</v-icon>
      </v-avatar>
      <div class="flex-grow-1">
        <h2 class="text-h6 font-weight-bold">{{ t('players.fastestLaps.title') }}</h2>
        <p class="text-caption text-medium-emphasis mb-0">{{ t('players.fastestLaps.subtitle') }}</p>
      </div>
      <v-chip variant="tonal" label>{{ laps.length }}</v-chip>
    </div>

    <v-divider />

    <v-table density="comfortable" hover>
      <thead>
        <tr>
          <th class="text-center" style="width: 56px">{{ t('standings.position') }}</th>
          <th>{{ t('events.track') }}</th>
          <th class="text-center">{{ t('leaderboard.car') }}</th>
          <th class="text-right text-no-wrap">{{ t('leaderboard.lapTime') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="lap in laps" :key="lap.trackKey">
          <td class="text-center">
            <span :class="podiumClass(lap.position) ?? 'text-medium-emphasis'">{{ lap.position }}</span>
          </td>
          <td><TrackName :track-id="lap.trackKey" /></td>
          <td class="text-center text-caption">{{ t(`cars.${lap.carId}`) }}</td>
          <td class="text-right">
            <span class="text-mono font-weight-bold">{{ lap.lapTime }}</span>
          </td>
        </tr>
      </tbody>
    </v-table>

    <v-divider />

    <v-card-actions class="pa-2">
      <v-spacer />
      <v-btn
        variant="text"
        color="primary"
        append-icon="mdi-chevron-right"
        :to="{ name: 'leaderboard' }"
      >
        {{ t('players.fastestLaps.allLink') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
/* Times are compared row by row: a fixed-width font (as in the leaderboard) keeps them aligned. */
.text-mono {
  font-family: monospace;
  font-size: 0.9rem;
}
</style>
